/**
 * Flat key-to-string dictionary for the customer-facing app.
 *
 * Keys are `dot.case`, grouped by surface (`enroll.*`, `label.*`, `err.*`, …).
 * Each key must exist under every locale in `SUPPORTED_LOCALES`, which the
 * `Messages` type enforces at compile time: add a key to the `en` literal
 * below, and TypeScript requires `es`, `ko` and `zh` to provide a matching entry.
 *
 * Validation error messages (`err.*`) live here too. `buildEnrollmentSchema(t)`
 * consumes them, so zod issue messages follow the active locale.
 *
 * Only the customer app (`@dearbill/app`) mounts `<I18nProvider>` and shows the
 * language selector. The admin app and the member portal read `MESSAGES.en`
 * directly, and the partner portal doesn't use these messages, so adding a
 * locale here doesn't affect the other SPAs.
 */
export const SUPPORTED_LOCALES = ["en", "es", "ko", "zh"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const en = {
  // Standing CMS-compliance disclaimer banner (shown in every environment). This
  // is the Blue Button API Terms of Service attribution notice and is required
  // verbatim. Its es/ko/zh renderings are machine-assisted drafts without legal
  // review.
  "disclaimer.banner":
    "This product uses the Blue Button APIs but is not endorsed or certified by the Centers for Medicare & Medicaid Services or the U.S. Department of Health and Human Services.",
  // Enrollment-flow footer legal link. Its sibling link reuses
  // `enroll.consent.privacy`; only the terms label differs.
  "footer.terms": "Terms & Conditions",

  "enroll.intro":
    "Tell us a little about yourself and your Medicare plan. Our team will follow up within 1–2 business days. Fields marked * are required.",
  "enroll.errorSummary": "Please fix the highlighted fields below before submitting.",
  "enroll.submitError": "Sorry, we couldn’t submit your form. Please try again.",

  // Success confirmation modal (shown after a successful submit). The partner
  // variant interpolates `{org}` as JSX in EnrollSuccessModal.tsx, mirroring
  // the `enroll.referral.banner` `{org}` pattern.
  "enroll.success.title": "Form successfully submitted",
  "enroll.success.individual": "Our team will follow up within 1-2 business days.",
  // Member-status line, shown in every variant: `{link}` is woven in as an
  // anchor to the server-provided memberStatusUrl (the same JSX weaving as the
  // partner sentence and the consent links).
  "enroll.success.memberStatus": "You can check the status of your enrollment on {link}.",
  "enroll.success.memberStatusLink": "the member page",
  // Referred variant: the patient may have completed the form themself (QR on
  // their own phone) or a staff member may have filled it in, and the copy must
  // be truthful for both. The member-status link is the primary destination;
  // the partner portal link is a secondary, staff-only way back.
  "enroll.success.partner": "This enrollment was submitted through {org}.",
  "enroll.success.staffPortal": "Staff member? Back to your portal",
  // Self-enroll variant (reached via a `?selfId=` QR link): names the org but,
  // unlike the partner variant, has no partner-portal link. `{org}` is woven as
  // JSX in EnrollSuccessModal.tsx.
  "enroll.success.self": "You enrolled through {org}.",
  "enroll.success.close": "Done",

  // Top-level form groups
  "section.patientInfo": "Member information",
  "section.prescriptionRequest": "Prescription & request",

  "section.personal": "Personal information",
  "section.permanent": "Permanent address",
  "section.mailing": "Mailing address",
  "section.contact": "Contact information",
  "section.medicare": "Medicare information",
  "section.pharmacy": "Preferred pharmacy",
  "section.prompt": "What’s prompting your request?",

  "label.fullName": "Full legal name",
  "label.firstName": "First name",
  "label.lastName": "Last name",
  "label.suffix": "Suffix (optional)",
  "label.pronouns": "Preferred pronouns (optional)",
  "label.dob": "Date of birth",
  "label.gender": "Gender",
  "label.street1": "Street address",
  "label.street2": "Apt, suite, unit (optional)",
  "label.city": "City",
  "label.state": "State",
  "label.zip": "ZIP code",
  "label.mailingDiffers": "My mailing address is different from my permanent address",
  "label.phone": "Primary Phone",
  "label.homePhone": "Secondary phone (optional)",
  "label.email": "Email (optional)",
  "label.preferredLanguage": "Preferred language for follow-up calls",
  "label.partDPlan": "Part D plan name",
  "label.mbi": "MBI (Medicare Beneficiary Identifier) (optional)",
  "label.planPhone": "Medicare plan phone",
  "label.planFax": "Medicare plan fax (optional)",
  "label.pharmacyName": "Pharmacy name",
  "label.pharmacyStreet": "Pharmacy street address",

  // Date of birth (segmented MM / DD / YYYY control)
  "dob.subLabel": "As shown on your Medicare card.",
  "dob.month": "Month",
  "dob.day": "Day",
  "dob.year": "Year",
  "dob.autoAdvance": "Typing two digits moves you to the next box automatically.",

  "ph.fullName": "As shown on your Medicare card",
  "ph.suffix": "Jr, Sr., III",
  "ph.zip": "XXXXX",
  "ph.phone": "(555) 555-1234",
  "ph.planPhone": "(800) 555-1234",
  "ph.email": "you@example.com",
  "ph.partDPlan": "e.g. AARP MedicareRx Preferred",
  "ph.mbi": "1EG4TE5MK73",
  "ph.pharmacyName": "e.g. Walgreens #4521",
  "ph.prompt": "What’s going on with your prescriptions or out-of-pocket costs?",

  // Hints / info-tooltip copy
  "hint.permanentStreet": "PO Boxes are not accepted. Medicare requires a physical address.",
  "hint.mbi": "Enter the 11-character code from your Medicare card (letters and numbers).",
  // Sub-label under the optional MBI input, like `dob.subLabel`.
  "mbi.subLabel": "Providing it now speeds up your plan enrollment",
  "tooltip.preferredLanguage": "The language our team will use when we contact you.",

  "select.placeholder": "Select…",
  "select.statePlaceholder": "—",
  "select.gender.male": "Male",
  "select.gender.female": "Female",
  "select.gender.non_binary": "Non-binary",
  "select.gender.prefer_not_to_say": "Prefer not to say",
  "select.language.english": "English",
  "select.language.spanish": "Spanish",
  "select.language.korean": "Korean",
  "select.language.mandarin": "Mandarin",
  "select.pronouns.placeholder": "Select…",
  "select.pronouns.she_her": "she/her",
  "select.pronouns.he_him": "he/him",
  "select.pronouns.they_them": "they/them",

  // Address autocomplete (Smarty) dropdown
  "addressAc.loading": "Searching addresses…",
  "addressAc.noMatches": "No matching addresses",
  "addressAc.suggestions": "Address suggestions",

  // Plan section: optional, state-gated Medicare-plan combobox.
  "section.plan": "Medicare plan",
  "label.plan": "Your Medicare plan (optional)",
  "ph.plan": "Search by plan, insurer, or contract number",
  "plan.hint": "Fill in your state above to choose your plan.",
  "plan.loading": "Loading plans…",
  "plan.noResults": "No plans found for your state.",
  "plan.noMatches": "No plans match your search.",
  "plan.refine": "Keep typing to narrow down the list.",

  "radio.yes": "Yes",
  "radio.no": "No",

  "btn.submit": "Submit",
  "btn.submitting": "Submitting…",

  // Consent checkbox label. Enroll.tsx replaces `{terms}` and `{privacy}` with
  // anchor links.
  "enroll.consent": "I agree to the {terms} and {privacy}.",
  "enroll.consent.terms": "Terms and Conditions",
  "enroll.consent.privacy": "Privacy Policy",
  // Pharmacy-referral only, mandatory. `{pharmacy}` is replaced with the referring
  // pharmacy's name via `.replace()` in Enroll.tsx (plain text, no link).
  "enroll.consent.pharmacy": "I agree to allow {pharmacy} to share my information with DearBill.",
  // Broker-referral only. The first is mandatory (client and server gate); the
  // second is optional and gates what the broker sees in their portal.
  // `{broker}` is replaced with the broker's name via `.replace()` in Enroll.tsx
  // (plain text, no link), the same mechanism as `{pharmacy}` above.
  "enroll.consent.broker":
    "I agree to appoint DearBill as my representative and to be contacted by DearBill.",
  "enroll.consent.brokerShare": "I agree to share my results with {broker}.",
  // Inline message under the permanent-address State select when the referring
  // broker isn't licensed there (hard block, client and server). The wording has
  // had no legal review.
  "enroll.broker.stateNotLicensed":
    "Your broker isn’t licensed in this state, so this form can’t be submitted through them.",
  // Optional SMS-consent checkbox (does not gate Submit; value is submitted).
  "enroll.consent.sms": "I agree to receive marketing and status updates via SMS.",
  // Full SMS messaging-terms disclosure. SmsDisclosure.tsx weaves `{privacy}` in as the
  // /privacy-policy link (with the `enroll.consent.privacy` text) and `{email}` as
  // the support mailto link, and emphasises the literal STOP/HELP keywords. The
  // wording has had no legal review.
  "enroll.consent.sms.disclosure":
    "By providing your mobile number, you agree to receive SMS messages from DearBill, including two-factor authentication (2FA) and security codes, account and enrollment notifications, customer care and support messages, and marketing and promotional messages. Message frequency varies based on your account activity and preferences. Message and data rates may apply. Reply STOP at any time to unsubscribe and stop receiving messages. Reply HELP for assistance, or contact us at {email}. For details on how we handle your information, see our {privacy}.",
  // Shown on the mandatory consent checkbox(es) when the user submits without ticking them.
  "enroll.consent.required": "Please accept to continue.",

  // Partner-referral banner. ReferralBanner.tsx weaves `{org}` and `{member}` in
  // as JSX, because the t() layer has no interpolation.
  "enroll.referral.banner": "Enrolling on behalf of {org}, referred by {member}.",
  "enroll.referral.expiresIn": "This referral link expires in",
  "enroll.referral.expired":
    "Your referral link expired — return to your partner portal and start a new enrollment.",
  "enroll.referral.invalid":
    "This referral link is invalid — return to your partner portal and start a new enrollment.",
  // Single-use gate: a used link blocks the form entirely (no fillable fields).
  "enroll.referral.used.title": "This enrollment link has already been used",
  "enroll.referral.used.body":
    "Each enrollment link can be used only once. Please ask the organization that referred you for a new link.",
  // Submit-time 409 `referral_used`: the link was claimed between page load and submit.
  "enroll.referral.usedError":
    "This enrollment link has already been used. Please request a new link from the organization that referred you.",

  // Self-enroll banner, reached via a per-org `?selfId=` QR link: org name only,
  // no referring member, no expiry. SelfEnrollBanner.tsx and SelfEnrollStrip.tsx
  // weave `{org}` in as JSX, like the referral banner.
  "enroll.self.banner": "Welcome! You’re enrolling through {org}.",
  "enroll.self.invalid":
    "This enrollment code is not valid — please rescan the code or contact the organization that gave it to you.",
  "enroll.self.unavailable":
    "This organization is not currently accepting self-enrollments. Please contact them for help.",

  // Enrollment-window explainer
  "info.trigger": "When can I enroll?",
  "info.hide": "Hide",
  "info.intro": "Enrollment in a Part D prescription drug plan is only allowed during set windows:",
  "info.win.turn65.label": "When you first turn 65",
  "info.win.turn65.body":
    "— a 7-month window covering the 3 months before, the month of, and the 3 months after your birthday month.",
  "info.win.fall.label": "Fall Open Enrollment",
  "info.win.fall.body": "— October 15 through December 7 each year.",
  "info.win.ma.label": "Medicare Advantage Switch Period",
  "info.win.ma.body":
    "— January 1 through March 31, but only if you are already in a Medicare Advantage plan.",
  "info.beware.label": "Be aware:",
  "info.beware.body": "A hospital stay does not automatically give you a new chance to enroll.",
  "info.snf":
    "If you are discharged from a skilled nursing facility, rehab center, or long-term care, you have 2 months after the month of discharge to sign up.",
  "info.hospital":
    "If you are discharged from a regular hospital after a short stay, you cannot enroll outside the windows listed above — even if you go home with a new costly prescription. Waiting until the next enrollment window may mean paying the full cost of your medication and a permanent late-enrollment penalty.",
  "info.contact": "If you have any questions, please reach out to DearBill directly.",

  // Visible "(required)" wording appended to required-field labels via
  // RequiredIndicatorContext, so required-ness doesn't rely on the asterisk
  // alone. Lowercase, because it renders inline as "(required)".
  "a11y.required": "required",

  "err.required": "Required",
  "err.invalidDate": "Enter a valid date in the past",
  // The "64" must match MIN_ENROLLMENT_AGE (lib/age.ts); the t() layer has no
  // parameter interpolation, so the number is hard-coded.
  "err.minAge": "Member must be at least 64 years old to enroll.",
  "err.selectOption": "Select an option",
  "err.selectState": "Select a state",
  "err.zip": "ZIP must be 5 digits (e.g. 90210 or 90210-1234)",
  "err.poBox":
    "Your permanent address can't be a PO box. You can use a PO box for your mailing address below",
  "err.streetIncomplete": "Please enter your full street address.",
  // Smarty ZIP ↔ city/state mismatch on submit; `zipMismatchMessage` fills
  // `{city}` (as typed) and `{state}` (the two-letter code).
  "err.zipMismatch": "This ZIP code doesn't match {city}, {state}. Please check it.",
  "err.phone": "Enter a 10-digit US phone number",
  "err.email": "Enter a valid email",
  // Cross-field uniqueness: this phone/email already appears on
  // another contact field of the same enrollment.
  "err.dupPhone": "This phone number is already used by another contact",
  "err.dupEmail": "This email is already used by another contact",
  "err.mbi": "Enter a valid 11-character Medicare MBI",
  "err.promptMin": "Please give us a sentence or two",

  "locale.aria": "Language",
  "locale.en": "English",
  "locale.es": "Español",
  "locale.ko": "한국어",
  "locale.zh": "中文",
  // Info tooltip next to the language selector.
  "locale.tooltip": "Changes the language of this page only.",

  // Two-tab "Contact us" modal: "Request Call" (the default tab) is the
  // request-a-call form and "Message Us" the email-message form. The time-window
  // and topic values sent to the backend are canonical English; only the labels
  // are localized. `contact.success` interpolates `{date}`, the requested date,
  // at render time.
  "contact.trigger": "Contact us",
  "contact.title": "Contact us",
  "contact.tabs.call": "Request Call",
  "contact.tabs.message": "Message Us",
  // Lead-in before the support-line tel: link; the number itself is not translated.
  "contact.callUs": "Prefer to call?",
  "contact.intro":
    "Have questions? You can request a call from a DearBill agent. Fill out this form to have us call you.",
  "contact.when": "When would you like us to call you?",
  "contact.date": "Date",
  "contact.window": "Time",
  "contact.windowPlaceholder": "Select a time",
  "contact.window.morning": "Morning: 9 AM to noon ET",
  "contact.window.afternoon": "Afternoon: Noon to 3 PM ET",
  "contact.window.lateAfternoon": "Late afternoon: 3 PM to 7 PM ET",
  "contact.firstName": "First name",
  "contact.lastName": "Last name",
  "contact.phone": "Phone number",
  "contact.zip": "ZIP code",
  "contact.consent": "By checking this box, you agree to have a DearBill agent contact you.",
  "contact.submit": "Send",
  "contact.submitting": "Sending…",
  "contact.cancel": "Cancel",
  "contact.success": "Thanks — a DearBill agent will call you on {date} in your chosen window.",
  "contact.done": "Done",
  "contact.err.date": "Choose a weekday between tomorrow and 30 days from now.",
  "contact.err.dateWeekend": "We call Monday through Friday — please choose a weekday.",
  "contact.err.window": "Select a time.",
  "contact.err.firstName": "Enter your first name.",
  "contact.err.lastName": "Enter your last name.",
  "contact.err.phone": "Enter your phone number.",
  "contact.err.zip": "Enter a 5-digit ZIP code.",
  "contact.err.consent": "Please check the box so a DearBill agent can contact you.",
  "contact.err.submit": "Please check your details and try again.",
  "contact.err.rateLimited": "You’ve recently requested a call — please try again later.",
  // "Message Us" tab
  "contact.email": "Email",
  "contact.topic": "Topic",
  "contact.topicPlaceholder": "Select a topic",
  "contact.message": "Message",
  "contact.success.message": "Thanks — we’ll be in touch.",
  "contact.err.contact": "Enter an email or phone number so we can reply.",
  "contact.err.topic": "Select a topic.",
  "contact.err.message": "Enter a message.",
  "contact.topics.enrollment": "Enrollment help",
  "contact.topics.eligibility": "Eligibility question",
  "contact.topics.technical": "Technical problem",
  "contact.topics.other": "Something else",
  // Book-a-call page (`/book-a-call`). Identity labels and the shared field
  // errors reuse the `contact.*` keys. The window values sent to the backend
  // are the canonical enum; only the labels are localized. `bookCall.success.body`
  // interpolates `{date}` and `{window}` as JSX.
  "bookCall.title": "Book a call with us",
  "bookCall.screening.title": "Request help with medicine costs",
  "bookCall.backToEstimate": "Back to your estimate",
  "bookCall.backToMedicines": "Back to your medicines",
  "bookCall.screening.intro":
    "Choose a time for a call about help with prescription costs. Your calculator answers are not sent with this request. Requesting a call does not enroll you in a program.",
  "bookCall.intro":
    "Tell us when to call and a DearBill specialist will walk you through your options. No paperwork needed for this call.",
  "bookCall.emailHint":
    "Optional — we’ll email your confirmation if it matches the email on your DearBill record.",
  "bookCall.date": "What day should we call?",
  "bookCall.window": "What time of day?",
  "bookCall.window.morning": "Morning",
  "bookCall.window.afternoon": "Afternoon",
  "bookCall.window.lateAfternoon": "Late afternoon",
  "bookCall.hours.morning": "9 AM to noon ET",
  "bookCall.hours.afternoon": "Noon to 3 PM ET",
  "bookCall.hours.lateAfternoon": "3 PM to 7 PM ET",
  "bookCall.submit": "Book my call",
  "bookCall.submitting": "Booking…",
  "bookCall.success.title": "You’re booked!",
  "bookCall.success.body":
    "You’re booked for {date}, {window}. We’ll confirm by text if you’ve agreed to texts from us.",
  "bookCall.err.window": "Choose a time of day.",
  "bookCall.err.firstName": "Enter your first name, up to 100 characters.",
  "bookCall.err.lastName": "Enter your last name, up to 100 characters.",
  "bookCall.err.phone": "Enter a valid 10-digit US phone number.",
  "bookCall.err.email": "Enter a valid email address, or leave it blank.",
  "bookCall.err.rateLimited":
    "Too many booking requests from this network this week. Please try again later or call us.",
  "bookCall.err.rateLimitedPhone":
    "This phone number has reached the weekly booking limit. Please try again next week or call us.",
  "bookCall.err.submit": "We couldn’t book your call. Please check your details and try again.",
  // Entry point in the "Request Call" tab of the "Contact us" modal.
  "bookCall.entry.lead": "Want to pick a day and time?",
  "bookCall.entry.link": "Book a call",
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;

const es: Messages = {
  // Franja de aviso de cumplimiento de CMS (se muestra en todos los entornos).
  "disclaimer.banner":
    "Este producto utiliza las API de Blue Button, pero no está respaldado ni certificado por los Centros de Servicios de Medicare y Medicaid ni por el Departamento de Salud y Servicios Humanos de EE. UU.",
  "footer.terms": "Términos y Condiciones",

  "enroll.intro":
    "Cuéntenos un poco sobre usted y su plan de Medicare. Nuestro equipo se comunicará con usted dentro de 1 a 2 días hábiles. Los campos marcados con * son obligatorios.",
  "enroll.errorSummary": "Por favor corrija los campos resaltados antes de enviar.",
  "enroll.submitError":
    "Lo sentimos, no pudimos enviar su formulario. Por favor inténtelo de nuevo.",

  // Modal de confirmación de envío exitoso. La variante de socio interpola
  // `{org}` como JSX en EnrollSuccessModal.tsx.
  "enroll.success.title": "Formulario enviado correctamente",
  "enroll.success.individual":
    "Nuestro equipo se comunicará con usted dentro de 1 a 2 días hábiles.",
  "enroll.success.memberStatus": "Puede consultar el estado de su inscripción en {link}.",
  "enroll.success.memberStatusLink": "la página del miembro",
  "enroll.success.partner": "Esta inscripción se envió a través de {org}.",
  "enroll.success.staffPortal": "¿Es miembro del personal? Regrese a su portal",
  "enroll.success.self": "Se inscribió a través de {org}.",
  "enroll.success.close": "Listo",

  // Top-level form groups
  "section.patientInfo": "Información del miembro",
  "section.prescriptionRequest": "Receta y solicitud",

  "section.personal": "Información personal",
  "section.permanent": "Dirección permanente",
  "section.mailing": "Dirección postal",
  "section.contact": "Información de contacto",
  "section.medicare": "Información de Medicare",
  "section.pharmacy": "Farmacia preferida",
  "section.prompt": "¿Qué motiva su solicitud?",

  "label.fullName": "Nombre legal completo",
  "label.firstName": "Nombre",
  "label.lastName": "Apellido",
  "label.suffix": "Sufijo (opcional)",
  "label.pronouns": "Pronombres preferidos (opcional)",
  "label.dob": "Fecha de nacimiento",
  "label.gender": "Género",
  "label.street1": "Dirección",
  "label.street2": "Apt., suite, unidad (opcional)",
  "label.city": "Ciudad",
  "label.state": "Estado",
  "label.zip": "Código postal",
  "label.mailingDiffers": "Mi dirección postal es diferente de mi dirección permanente",
  "label.phone": "Teléfono principal",
  "label.homePhone": "Teléfono secundario (opcional)",
  "label.email": "Correo electrónico (opcional)",
  "label.preferredLanguage": "Idioma preferido para las llamadas de seguimiento",
  "label.partDPlan": "Nombre del plan de la Parte D",
  "label.mbi": "MBI (Identificador de Beneficiario de Medicare) (opcional)",
  "label.planPhone": "Teléfono del plan de Medicare",
  "label.planFax": "Fax del plan de Medicare (opcional)",
  "label.pharmacyName": "Nombre de la farmacia",
  "label.pharmacyStreet": "Dirección de la farmacia",

  // Fecha de nacimiento (control segmentado MM / DD / AAAA)
  "dob.subLabel": "Como aparece en su tarjeta de Medicare.",
  "dob.month": "Mes",
  "dob.day": "Día",
  "dob.year": "Año",
  "dob.autoAdvance": "Al escribir dos dígitos pasará automáticamente a la siguiente casilla.",

  "ph.fullName": "Como aparece en su tarjeta de Medicare",
  "ph.suffix": "Jr, Sr., III",
  "ph.zip": "XXXXX",
  "ph.phone": "(555) 555-1234",
  "ph.planPhone": "(800) 555-1234",
  "ph.email": "usted@ejemplo.com",
  "ph.partDPlan": "p. ej. AARP MedicareRx Preferred",
  "ph.mbi": "1EG4TE5MK73",
  "ph.pharmacyName": "p. ej. Walgreens #4521",
  "ph.prompt": "¿Qué está pasando con sus recetas o sus gastos de bolsillo?",

  // Hints / texto de la información emergente
  "hint.permanentStreet":
    "No se aceptan apartados postales. Medicare requiere una dirección física.",
  "hint.mbi": "Ingrese el código de 11 caracteres de su tarjeta de Medicare (letras y números).",
  "mbi.subLabel": "Proporcionarlo ahora acelera su inscripción en el plan",
  "tooltip.preferredLanguage": "El idioma que usará nuestro equipo cuando lo contactemos.",

  "select.placeholder": "Seleccione…",
  "select.statePlaceholder": "—",
  "select.gender.male": "Masculino",
  "select.gender.female": "Femenino",
  "select.gender.non_binary": "No binario",
  "select.gender.prefer_not_to_say": "Prefiero no decir",
  "select.language.english": "Inglés",
  "select.language.spanish": "Español",
  "select.language.korean": "Coreano",
  "select.language.mandarin": "Mandarín",
  "select.pronouns.placeholder": "Seleccionar…",
  "select.pronouns.she_her": "she/her",
  "select.pronouns.he_him": "he/him",
  "select.pronouns.they_them": "they/them",

  // Address autocomplete (Smarty) dropdown
  "addressAc.loading": "Buscando direcciones…",
  "addressAc.noMatches": "No se encontraron direcciones",
  "addressAc.suggestions": "Sugerencias de direcciones",

  // Plan section
  "section.plan": "Plan de Medicare",
  "label.plan": "Su plan de Medicare (opcional)",
  "ph.plan": "Busque por plan, aseguradora o número de contrato",
  "plan.hint": "Complete su estado arriba para elegir su plan.",
  "plan.loading": "Cargando planes…",
  "plan.noResults": "No se encontraron planes para su estado.",
  "plan.noMatches": "Ningún plan coincide con su búsqueda.",
  "plan.refine": "Siga escribiendo para acotar la lista.",

  "radio.yes": "Sí",
  "radio.no": "No",

  "btn.submit": "Enviar",
  "btn.submitting": "Enviando…",

  // Consent checkbox label. Enroll.tsx replaces `{terms}` and `{privacy}` with
  // anchor links.
  "enroll.consent": "Acepto los {terms} y la {privacy}.",
  "enroll.consent.terms": "Términos y Condiciones",
  "enroll.consent.privacy": "Política de Privacidad",
  // Solo para referido de farmacia, obligatorio. `{pharmacy}` se reemplaza con el
  // nombre de la farmacia mediante `.replace()` en Enroll.tsx (texto sin enlace).
  "enroll.consent.pharmacy": "Acepto permitir que {pharmacy} comparta mi información con DearBill.",
  "enroll.consent.broker":
    "Acepto designar a DearBill como mi representante y que DearBill se comunique conmigo.",
  "enroll.consent.brokerShare": "Acepto compartir mis resultados con {broker}.",
  "enroll.broker.stateNotLicensed":
    "Su agente no tiene licencia en este estado, por lo que este formulario no puede enviarse a través de él.",
  "enroll.consent.sms": "Acepto recibir mensajes de marketing y actualizaciones de estado por SMS.",
  // Borrador sin revisión legal. STOP/HELP se mantienen en inglés (las
  // operadoras reconocen las palabras clave literales en inglés).
  "enroll.consent.sms.disclosure":
    "Al proporcionar su número de teléfono móvil, acepta recibir mensajes SMS de DearBill, incluidos códigos de autenticación de dos factores (2FA) y de seguridad, notificaciones de su cuenta e inscripción, mensajes de atención y soporte al cliente, y mensajes de marketing y promocionales. La frecuencia de los mensajes varía según la actividad y las preferencias de su cuenta. Pueden aplicarse tarifas de mensajes y datos. Responda STOP en cualquier momento para cancelar la suscripción y dejar de recibir mensajes. Responda HELP para obtener ayuda, o contáctenos en {email}. Para obtener detalles sobre cómo manejamos su información, consulte nuestra {privacy}.",
  "enroll.consent.required": "Por favor acepte para continuar.",

  // Partner-referral banner
  "enroll.referral.banner": "Inscribiendo en nombre de {org}, referido por {member}.",
  "enroll.referral.expiresIn": "Este enlace de referido vence en",
  "enroll.referral.expired":
    "Su enlace de referido venció — regrese a su portal de socios e inicie una nueva inscripción.",
  "enroll.referral.invalid":
    "Este enlace de referido no es válido — regrese a su portal de socios e inicie una nueva inscripción.",
  // Uso único: un enlace usado bloquea el formulario (sin campos editables).
  "enroll.referral.used.title": "Este enlace de inscripción ya fue utilizado",
  "enroll.referral.used.body":
    "Cada enlace de inscripción se puede usar solo una vez. Solicite un nuevo enlace a la organización que lo refirió.",
  // 409 `referral_used` al enviar: el enlace fue reclamado entre la carga y el envío.
  "enroll.referral.usedError":
    "Este enlace de inscripción ya fue utilizado. Solicite un nuevo enlace a la organización que lo refirió.",

  // Banner de autoinscripción. Solo el nombre de la organización, sin miembro
  // que refiere ni vencimiento. `{org}` se teje como JSX.
  "enroll.self.banner": "¡Bienvenido! Se está inscribiendo a través de {org}.",
  "enroll.self.invalid":
    "Este código de inscripción no es válido — vuelva a escanear el código o comuníquese con la organización que se lo entregó.",
  "enroll.self.unavailable":
    "Esta organización no está aceptando autoinscripciones en este momento. Comuníquese con ellos para obtener ayuda.",

  // Enrollment-window explainer
  "info.trigger": "¿Cuándo puedo inscribirme?",
  "info.hide": "Ocultar",
  "info.intro":
    "La inscripción en un plan de recetas de la Parte D solo se permite durante ciertas ventanas:",
  "info.win.turn65.label": "Cuando cumple 65 años por primera vez",
  "info.win.turn65.body":
    "— una ventana de 7 meses que cubre los 3 meses antes, el mes del, y los 3 meses después del mes de su cumpleaños.",
  "info.win.fall.label": "Inscripción Abierta de Otoño",
  "info.win.fall.body": "— del 15 de octubre al 7 de diciembre de cada año.",
  "info.win.ma.label": "Período de Cambio de Medicare Advantage",
  "info.win.ma.body":
    "— del 1 de enero al 31 de marzo, pero solo si ya está inscrito en un plan de Medicare Advantage.",
  "info.beware.label": "Tenga en cuenta:",
  "info.beware.body":
    "Una estancia en el hospital no le da automáticamente una nueva oportunidad para inscribirse.",
  "info.snf":
    "Si le dan de alta de una instalación de enfermería especializada, centro de rehabilitación o cuidado a largo plazo, tiene 2 meses después del mes del alta para inscribirse.",
  "info.hospital":
    "Si le dan de alta de un hospital regular después de una estancia corta, no puede inscribirse fuera de las ventanas indicadas arriba — incluso si va a casa con un nuevo medicamento costoso. Esperar hasta la próxima ventana de inscripción puede significar pagar el costo total de su medicamento y una multa permanente por inscripción tardía.",
  "info.contact": "Si tiene alguna pregunta, comuníquese directamente con DearBill.",

  "a11y.required": "obligatorio",

  "err.required": "Obligatorio",
  "err.invalidDate": "Ingrese una fecha válida en el pasado",
  // El "64" debe mantenerse sincronizado con MIN_ENROLLMENT_AGE (lib/age.ts).
  "err.minAge": "El miembro debe tener al menos 64 años para inscribirse.",
  "err.selectOption": "Seleccione una opción",
  "err.selectState": "Seleccione un estado",
  "err.zip": "El código postal debe tener 5 dígitos (p. ej. 90210 o 90210-1234)",
  "err.poBox":
    "Su dirección permanente no puede ser un apartado postal. Puede usar un apartado postal para su dirección de correo más abajo",
  "err.streetIncomplete": "Ingrese su dirección completa (número y calle).",
  "err.zipMismatch": "Este código postal no corresponde a {city}, {state}. Verifíquelo.",
  "err.phone": "Ingrese un número de teléfono de EE. UU. de 10 dígitos",
  "err.email": "Ingrese un correo electrónico válido",
  // Unicidad entre campos: este teléfono/correo ya aparece en otro
  // contacto de la misma inscripción.
  "err.dupPhone": "Este número de teléfono ya lo usa otro contacto",
  "err.dupEmail": "Este correo electrónico ya lo usa otro contacto",
  "err.mbi": "Ingrese un MBI de Medicare válido de 11 caracteres",
  "err.promptMin": "Cuéntenos en una o dos oraciones",

  "locale.aria": "Idioma",
  "locale.en": "English",
  "locale.es": "Español",
  "locale.ko": "한국어",
  "locale.zh": "中文",
  "locale.tooltip": "Cambia el idioma solo de esta página.",

  // Modal "Contáctenos". Los valores de horario enviados al backend son
  // canónicos; solo las etiquetas se traducen. Traducción asistida por máquina
  // y provisional, como los textos legales.
  "contact.trigger": "Contáctenos",
  "contact.title": "Contáctenos",
  "contact.tabs.call": "Solicitar llamada",
  "contact.tabs.message": "Envíenos un mensaje",
  "contact.callUs": "¿Prefiere llamar?",
  "contact.intro":
    "¿Tiene preguntas? Puede solicitar una llamada de un agente de DearBill. Complete este formulario para que le llamemos.",
  "contact.when": "¿Cuándo le gustaría que le llamemos?",
  "contact.date": "Fecha",
  "contact.window": "Hora",
  "contact.windowPlaceholder": "Seleccione una hora",
  "contact.window.morning": "Mañana: de 9 AM a mediodía, hora del Este",
  "contact.window.afternoon": "Tarde: de mediodía a 3 PM, hora del Este",
  "contact.window.lateAfternoon": "Media tarde: de 3 PM a 7 PM, hora del Este",
  "contact.firstName": "Nombre",
  "contact.lastName": "Apellido",
  "contact.phone": "Número de teléfono",
  "contact.zip": "Código postal",
  "contact.consent":
    "Al marcar esta casilla, usted acepta que un agente de DearBill se comunique con usted.",
  "contact.submit": "Enviar",
  "contact.submitting": "Enviando…",
  "contact.cancel": "Cancelar",
  "contact.success":
    "Gracias — un agente de DearBill le llamará el {date} en el horario que eligió.",
  "contact.done": "Listo",
  "contact.err.date": "Elija un día de semana entre mañana y 30 días a partir de hoy.",
  "contact.err.dateWeekend": "Llamamos de lunes a viernes — elija un día de semana.",
  "contact.err.window": "Seleccione una hora.",
  "contact.err.firstName": "Ingrese su nombre.",
  "contact.err.lastName": "Ingrese su apellido.",
  "contact.err.phone": "Ingrese su número de teléfono.",
  "contact.err.zip": "Ingrese un código postal de 5 dígitos.",
  "contact.err.consent":
    "Marque la casilla para que un agente de DearBill pueda comunicarse con usted.",
  "contact.err.submit": "Verifique sus datos e inténtelo de nuevo.",
  "contact.err.rateLimited": "Solicitó una llamada hace poco — inténtelo de nuevo más tarde.",
  // Pestaña "Envíenos un mensaje"
  "contact.email": "Correo electrónico",
  "contact.topic": "Tema",
  "contact.topicPlaceholder": "Seleccione un tema",
  "contact.message": "Mensaje",
  "contact.success.message": "Gracias, nos pondremos en contacto.",
  "contact.err.contact": "Ingrese un correo electrónico o teléfono para poder responderle.",
  "contact.err.topic": "Seleccione un tema.",
  "contact.err.message": "Ingrese un mensaje.",
  "contact.topics.enrollment": "Ayuda con la inscripción",
  "contact.topics.eligibility": "Pregunta sobre elegibilidad",
  "contact.topics.technical": "Problema técnico",
  "contact.topics.other": "Otra cosa",
  // Página para programar una llamada. Traducción asistida por máquina y
  // provisional, como el modal "Contáctenos".
  "bookCall.title": "Programe una llamada con nosotros",
  "bookCall.screening.title": "Solicite ayuda con los costos de sus medicamentos",
  "bookCall.backToEstimate": "Volver a su estimación",
  "bookCall.backToMedicines": "Volver a sus medicamentos",
  "bookCall.screening.intro":
    "Elija un horario para una llamada sobre ayuda con los costos de sus medicamentos. Sus respuestas de la calculadora no se envían con esta solicitud. Solicitar una llamada no lo inscribe en un programa.",
  "bookCall.intro":
    "Díganos cuándo llamarle y un especialista de DearBill le explicará sus opciones. No necesita papeleo para esta llamada.",
  "bookCall.emailHint":
    "Opcional — le enviaremos la confirmación por correo si coincide con el correo de su registro en DearBill.",
  "bookCall.date": "¿Qué día debemos llamarle?",
  "bookCall.window": "¿A qué hora del día?",
  "bookCall.window.morning": "Mañana",
  "bookCall.window.afternoon": "Tarde",
  "bookCall.window.lateAfternoon": "Media tarde",
  "bookCall.hours.morning": "De 9 AM a mediodía, hora del Este",
  "bookCall.hours.afternoon": "De mediodía a 3 PM, hora del Este",
  "bookCall.hours.lateAfternoon": "De 3 PM a 7 PM, hora del Este",
  "bookCall.submit": "Programar mi llamada",
  "bookCall.submitting": "Programando…",
  "bookCall.success.title": "¡Su llamada está programada!",
  "bookCall.success.body":
    "Su llamada está programada para el {date}, por la {window}. Le confirmaremos por mensaje de texto si aceptó recibir mensajes de texto de nosotros.",
  "bookCall.err.window": "Elija una hora del día.",
  "bookCall.err.firstName": "Ingrese su nombre, de hasta 100 caracteres.",
  "bookCall.err.lastName": "Ingrese su apellido, de hasta 100 caracteres.",
  "bookCall.err.phone": "Ingrese un número de teléfono válido de EE. UU. de 10 dígitos.",
  "bookCall.err.email": "Ingrese un correo electrónico válido o déjelo en blanco.",
  "bookCall.err.rateLimited":
    "Demasiadas solicitudes de llamada desde esta red esta semana. Inténtelo de nuevo más tarde o llámenos.",
  "bookCall.err.rateLimitedPhone":
    "Este número de teléfono alcanzó el límite semanal de llamadas programadas. Inténtelo de nuevo la próxima semana o llámenos.",
  "bookCall.err.submit":
    "No pudimos programar su llamada. Verifique sus datos e inténtelo de nuevo.",
  "bookCall.entry.lead": "¿Quiere elegir el día y la hora?",
  "bookCall.entry.link": "Programe una llamada",
};

/**
 * Korean (한국어), a machine-assisted, best-effort translation.
 *
 * The legal and compliance-heavy strings (`enroll.consent`,
 * `enroll.consent.terms`, `enroll.consent.privacy`, `enroll.consent.pharmacy`,
 * `enroll.consent.broker`, `enroll.consent.brokerShare`, `enroll.consent.sms`,
 * `enroll.consent.sms.disclosure` and the `info.*` Medicare enrollment-window
 * block) require professional or legal review before production use.
 * Interpolation tokens (`{terms}`, `{privacy}`, `{email}`, `{org}`, `{member}`,
 * `{link}`, `{pharmacy}`, `{broker}`) and the literal STOP/HELP keywords stay
 * verbatim.
 */
const ko: Messages = {
  "disclaimer.banner":
    "이 제품은 Blue Button API를 사용하지만 메디케어 및 메디케이드 서비스 센터(CMS) 또는 미국 보건복지부(HHS)의 보증이나 인증을 받지 않았습니다.",
  "footer.terms": "이용 약관",

  "enroll.intro":
    "본인과 Medicare 플랜에 대해 간단히 알려주세요. 저희 팀이 영업일 기준 1~2일 이내에 연락드립니다. *로 표시된 항목은 필수입니다.",
  "enroll.errorSummary": "제출하기 전에 아래 강조 표시된 항목을 수정해 주세요.",
  "enroll.submitError": "죄송합니다. 양식을 제출하지 못했습니다. 다시 시도해 주세요.",

  "enroll.success.title": "양식이 성공적으로 제출되었습니다",
  "enroll.success.individual": "저희 팀이 영업일 기준 1~2일 이내에 연락드립니다.",
  "enroll.success.memberStatus": "{link}에서 신청 상태를 확인할 수 있습니다.",
  "enroll.success.memberStatusLink": "회원 페이지",
  "enroll.success.partner": "이 신청은 {org}을(를) 통해 제출되었습니다.",
  "enroll.success.staffPortal": "직원이신가요? 포털로 돌아가기",
  "enroll.success.self": "{org}을(를) 통해 등록하셨습니다.",
  "enroll.success.close": "완료",

  "section.patientInfo": "회원 정보",
  "section.prescriptionRequest": "처방 및 요청",

  "section.personal": "개인 정보",
  "section.permanent": "영구 주소",
  "section.mailing": "우편 주소",
  "section.contact": "연락처 정보",
  "section.medicare": "Medicare 정보",
  "section.pharmacy": "선호 약국",
  "section.prompt": "무엇 때문에 요청하시나요?",

  "label.fullName": "법적 전체 이름",
  "label.firstName": "이름",
  "label.lastName": "성",
  "label.suffix": "접미어 (선택 사항)",
  "label.pronouns": "선호하는 대명사 (선택 사항)",
  "label.dob": "생년월일",
  "label.gender": "성별",
  "label.street1": "도로명 주소",
  "label.street2": "아파트, 호수, 유닛 (선택 사항)",
  "label.city": "도시",
  "label.state": "주",
  "label.zip": "우편번호",
  "label.mailingDiffers": "우편 주소가 영구 주소와 다릅니다",
  "label.phone": "기본 전화번호",
  "label.homePhone": "보조 전화번호 (선택 사항)",
  "label.email": "이메일 (선택 사항)",
  "label.preferredLanguage": "후속 통화 시 선호하는 언어",
  "label.partDPlan": "Part D 플랜 이름",
  "label.mbi": "MBI (Medicare 수혜자 식별번호) (선택 사항)",
  "label.planPhone": "Medicare 플랜 전화번호",
  "label.planFax": "Medicare 플랜 팩스 (선택 사항)",
  "label.pharmacyName": "약국 이름",
  "label.pharmacyStreet": "약국 도로명 주소",

  "dob.subLabel": "Medicare 카드에 표시된 대로.",
  "dob.month": "월",
  "dob.day": "일",
  "dob.year": "연도",
  "dob.autoAdvance": "두 자리를 입력하면 다음 칸으로 자동 이동합니다.",

  "ph.fullName": "Medicare 카드에 표시된 대로",
  "ph.suffix": "Jr, Sr., III",
  "ph.zip": "XXXXX",
  "ph.phone": "(555) 555-1234",
  "ph.planPhone": "(800) 555-1234",
  "ph.email": "you@example.com",
  "ph.partDPlan": "예: AARP MedicareRx Preferred",
  "ph.mbi": "1EG4TE5MK73",
  "ph.pharmacyName": "예: Walgreens #4521",
  "ph.prompt": "처방약이나 본인 부담 비용과 관련해 어떤 일이 있나요?",

  "hint.permanentStreet": "사서함(PO Box)은 사용할 수 없습니다. Medicare는 실제 주소를 요구합니다.",
  "hint.mbi": "Medicare 카드에 있는 11자리 코드를 입력하세요 (영문자와 숫자).",
  "mbi.subLabel": "지금 입력하시면 플랜 등록이 더 빨라집니다",
  "tooltip.preferredLanguage": "저희 팀이 연락드릴 때 사용할 언어입니다.",

  "select.placeholder": "선택…",
  "select.statePlaceholder": "—",
  "select.gender.male": "남성",
  "select.gender.female": "여성",
  "select.gender.non_binary": "논바이너리",
  "select.gender.prefer_not_to_say": "밝히고 싶지 않음",
  "select.language.english": "영어",
  "select.language.spanish": "스페인어",
  "select.language.korean": "한국어",
  "select.language.mandarin": "중국어(만다린)",
  "select.pronouns.placeholder": "선택…",
  "select.pronouns.she_her": "she/her",
  "select.pronouns.he_him": "he/him",
  "select.pronouns.they_them": "they/them",

  // Address autocomplete (Smarty) dropdown
  "addressAc.loading": "주소 검색 중…",
  "addressAc.noMatches": "일치하는 주소가 없습니다",
  "addressAc.suggestions": "주소 추천",

  // Plan section
  "section.plan": "Medicare 플랜",
  "label.plan": "Medicare 플랜 (선택 사항)",
  "ph.plan": "플랜, 보험사 또는 계약 번호로 검색",
  "plan.hint": "플랜을 선택하려면 위에 주를 입력하세요.",
  "plan.loading": "플랜을 불러오는 중…",
  "plan.noResults": "해당 주에서 이용 가능한 플랜이 없습니다.",
  "plan.noMatches": "검색과 일치하는 플랜이 없습니다.",
  "plan.refine": "계속 입력하여 목록을 좁혀 보세요.",

  "radio.yes": "예",
  "radio.no": "아니요",

  "btn.submit": "제출",
  "btn.submitting": "제출 중…",

  "enroll.consent": "{terms} 및 {privacy}에 동의합니다.",
  "enroll.consent.terms": "이용 약관",
  "enroll.consent.privacy": "개인정보 처리방침",
  "enroll.consent.pharmacy": "{pharmacy}이(가) 제 정보를 DearBill과 공유하는 것에 동의합니다.",
  "enroll.consent.broker":
    "DearBill을 제 대리인으로 지정하고 DearBill로부터 연락을 받는 데 동의합니다.",
  "enroll.consent.brokerShare": "제 결과를 {broker}과(와) 공유하는 데 동의합니다.",
  "enroll.broker.stateNotLicensed":
    "귀하의 브로커는 이 주에서 면허가 없으므로 이 양식을 해당 브로커를 통해 제출할 수 없습니다.",
  "enroll.consent.sms": "SMS를 통해 마케팅 및 상태 업데이트를 받는 데 동의합니다.",
  "enroll.consent.sms.disclosure":
    "휴대폰 번호를 제공하시면 DearBill로부터 SMS 메시지를 받는 데 동의하게 됩니다. 여기에는 2단계 인증(2FA) 및 보안 코드, 계정 및 등록 알림, 고객 지원 메시지, 마케팅 및 프로모션 메시지가 포함됩니다. 메시지 빈도는 계정 활동 및 설정에 따라 달라집니다. 메시지 및 데이터 요금이 부과될 수 있습니다. 구독을 취소하고 메시지 수신을 중단하려면 언제든지 STOP을 회신하세요. 도움이 필요하면 HELP를 회신하시거나 {email}로 문의하세요. 당사가 귀하의 정보를 처리하는 방법에 대한 자세한 내용은 {privacy}을(를) 참조하세요.",
  "enroll.consent.required": "계속하려면 동의해 주세요.",

  "enroll.referral.banner": "{member}의 의뢰로 {org}을(를) 대신하여 등록합니다.",
  "enroll.referral.expiresIn": "이 의뢰 링크는 다음 시간 후 만료됩니다",
  "enroll.referral.expired":
    "의뢰 링크가 만료되었습니다 — 파트너 포털로 돌아가 새 신청을 시작하세요.",
  "enroll.referral.invalid":
    "이 의뢰 링크는 유효하지 않습니다 — 파트너 포털로 돌아가 새 신청을 시작하세요.",
  "enroll.referral.used.title": "이 등록 링크는 이미 사용되었습니다",
  "enroll.referral.used.body":
    "각 등록 링크는 한 번만 사용할 수 있습니다. 의뢰해 준 조직에 새 링크를 요청하세요.",
  "enroll.referral.usedError":
    "이 등록 링크는 이미 사용되었습니다. 의뢰해 준 조직에 새 링크를 요청하세요.",

  "enroll.self.banner": "환영합니다! {org}을(를) 통해 등록하고 있습니다.",
  "enroll.self.invalid":
    "이 등록 코드는 유효하지 않습니다 — 코드를 다시 스캔하거나 코드를 제공한 조직에 문의하세요.",
  "enroll.self.unavailable":
    "이 조직은 현재 자가 등록을 받고 있지 않습니다. 도움이 필요하면 해당 조직에 문의하세요.",

  "info.trigger": "언제 등록할 수 있나요?",
  "info.hide": "숨기기",
  "info.intro": "Part D 처방약 플랜 등록은 정해진 기간에만 허용됩니다:",
  "info.win.turn65.label": "만 65세가 되는 시점",
  "info.win.turn65.body":
    "— 생일이 있는 달의 3개월 전, 생일이 있는 달, 그리고 3개월 후를 포함하는 7개월 기간.",
  "info.win.fall.label": "가을 공개 등록",
  "info.win.fall.body": "— 매년 10월 15일부터 12월 7일까지.",
  "info.win.ma.label": "Medicare Advantage 전환 기간",
  "info.win.ma.body":
    "— 1월 1일부터 3월 31일까지, 단 이미 Medicare Advantage 플랜에 가입한 경우에만 해당됩니다.",
  "info.beware.label": "유의 사항:",
  "info.beware.body": "입원한다고 해서 자동으로 새로운 등록 기회가 주어지는 것은 아닙니다.",
  "info.snf":
    "전문 요양 시설, 재활 센터 또는 장기 요양 시설에서 퇴원하는 경우, 퇴원한 달 이후 2개월 이내에 등록할 수 있습니다.",
  "info.hospital":
    "단기 입원 후 일반 병원에서 퇴원하는 경우, 새로운 고가의 처방약을 가지고 귀가하더라도 위에 나열된 기간 외에는 등록할 수 없습니다. 다음 등록 기간까지 기다리면 약값 전액과 영구적인 지연 등록 벌금을 내야 할 수 있습니다.",
  "info.contact": "궁금한 점이 있으면 DearBill에 직접 문의하세요.",

  "a11y.required": "필수",

  "err.required": "필수 항목",
  "err.invalidDate": "과거의 유효한 날짜를 입력하세요",
  "err.minAge": "회원은 등록하려면 최소 64세 이상이어야 합니다.",
  "err.selectOption": "옵션을 선택하세요",
  "err.selectState": "주를 선택하세요",
  "err.zip": "우편번호는 5자리여야 합니다 (예: 90210 또는 90210-1234)",
  "err.poBox":
    "영구 주소는 사서함(PO Box)일 수 없습니다. 아래의 우편 주소에는 사서함을 사용할 수 있습니다",
  "err.streetIncomplete": "전체 도로명 주소를 입력하세요.",
  "err.zipMismatch": "이 우편번호는 {city}, {state}와 일치하지 않습니다. 확인해 주세요.",
  "err.phone": "10자리 미국 전화번호를 입력하세요",
  "err.email": "유효한 이메일을 입력하세요",
  "err.dupPhone": "이 전화번호는 이미 다른 연락처에서 사용 중입니다",
  "err.dupEmail": "이 이메일은 이미 다른 연락처에서 사용 중입니다",
  "err.mbi": "유효한 11자리 Medicare MBI를 입력하세요",
  "err.promptMin": "한두 문장으로 알려주세요",

  "locale.aria": "언어",
  "locale.en": "English",
  "locale.es": "Español",
  "locale.ko": "한국어",
  "locale.zh": "中文",
  "locale.tooltip": "이 페이지의 언어만 변경합니다.",

  // "문의하기" 모달. 시간대 값은 canonical 값 그대로 전송됩니다.
  // 기계 번역 잠정본이며 전문 검수를 거치지 않았습니다.
  "contact.trigger": "문의하기",
  "contact.title": "문의하기",
  "contact.tabs.call": "전화 요청",
  "contact.tabs.message": "메시지 보내기",
  "contact.callUs": "전화가 편하신가요?",
  "contact.intro":
    "궁금한 점이 있으신가요? DearBill 상담원에게 전화를 요청할 수 있습니다. 이 양식을 작성해 주시면 전화드리겠습니다.",
  "contact.when": "언제 전화드리면 될까요?",
  "contact.date": "날짜",
  "contact.window": "시간",
  "contact.windowPlaceholder": "시간을 선택하세요",
  "contact.window.morning": "오전: 동부 시간 오전 9시~정오",
  "contact.window.afternoon": "오후: 동부 시간 정오~오후 3시",
  "contact.window.lateAfternoon": "늦은 오후: 동부 시간 오후 3시~7시",
  "contact.firstName": "이름",
  "contact.lastName": "성",
  "contact.phone": "전화번호",
  "contact.zip": "우편번호",
  "contact.consent": "이 확인란을 선택하면 DearBill 상담원이 연락하는 데 동의하는 것입니다.",
  "contact.submit": "보내기",
  "contact.submitting": "보내는 중…",
  "contact.cancel": "취소",
  "contact.success": "감사합니다 — DearBill 상담원이 {date}에 선택하신 시간대에 전화드리겠습니다.",
  "contact.done": "완료",
  "contact.err.date": "내일부터 30일 이내의 평일 날짜를 선택하세요.",
  "contact.err.dateWeekend": "통화는 월요일~금요일에 가능합니다 — 평일을 선택하세요.",
  "contact.err.window": "시간을 선택하세요.",
  "contact.err.firstName": "이름을 입력하세요.",
  "contact.err.lastName": "성을 입력하세요.",
  "contact.err.phone": "전화번호를 입력하세요.",
  "contact.err.zip": "5자리 우편번호를 입력하세요.",
  "contact.err.consent": "DearBill 상담원이 연락할 수 있도록 확인란을 선택하세요.",
  "contact.err.submit": "정보를 확인한 후 다시 시도하세요.",
  "contact.err.rateLimited": "최근에 이미 통화를 요청하셨습니다 — 잠시 후 다시 시도해 주세요.",
  // "메시지 보내기" 탭
  "contact.email": "이메일",
  "contact.topic": "주제",
  "contact.topicPlaceholder": "주제를 선택하세요",
  "contact.message": "메시지",
  "contact.success.message": "감사합니다 — 곧 연락드리겠습니다.",
  "contact.err.contact": "회신할 수 있도록 이메일 또는 전화번호를 입력하세요.",
  "contact.err.topic": "주제를 선택하세요.",
  "contact.err.message": "메시지를 입력하세요.",
  "contact.topics.enrollment": "등록 도움",
  "contact.topics.eligibility": "자격 관련 질문",
  "contact.topics.technical": "기술적 문제",
  "contact.topics.other": "기타",
  // 통화 예약 페이지. 기계 번역 잠정본이며 전문 검수를 거치지 않았습니다.
  "bookCall.title": "통화 예약하기",
  "bookCall.screening.title": "처방약 비용에 대한 도움 요청",
  "bookCall.backToEstimate": "예상 비용으로 돌아가기",
  "bookCall.backToMedicines": "복용 약으로 돌아가기",
  "bookCall.screening.intro":
    "처방약 비용 지원에 대해 상담할 시간을 선택하세요. 계산기에 입력한 내용은 이 요청과 함께 전송되지 않습니다. 통화를 요청해도 프로그램에 가입되지 않습니다.",
  "bookCall.intro":
    "언제 전화드리면 될지 알려 주시면 DearBill 전문 상담원이 선택 가능한 방법을 안내해 드립니다. 이 통화에는 서류가 필요하지 않습니다.",
  "bookCall.emailHint":
    "선택 사항 — DearBill 기록에 있는 이메일과 일치하면 예약 확인을 이메일로 보내 드립니다.",
  "bookCall.date": "어느 날 전화드릴까요?",
  "bookCall.window": "어느 시간대가 좋으세요?",
  "bookCall.window.morning": "오전",
  "bookCall.window.afternoon": "오후",
  "bookCall.window.lateAfternoon": "늦은 오후",
  "bookCall.hours.morning": "동부 시간 오전 9시~정오",
  "bookCall.hours.afternoon": "동부 시간 정오~오후 3시",
  "bookCall.hours.lateAfternoon": "동부 시간 오후 3시~7시",
  "bookCall.submit": "통화 예약하기",
  "bookCall.submitting": "예약 중…",
  "bookCall.success.title": "예약되었습니다!",
  "bookCall.success.body":
    "{date} {window}에 통화가 예약되었습니다. 문자 수신에 동의하셨다면 문자로 확인해 드리겠습니다.",
  "bookCall.err.window": "시간대를 선택하세요.",
  "bookCall.err.firstName": "이름을 100자 이내로 입력하세요.",
  "bookCall.err.lastName": "성을 100자 이내로 입력하세요.",
  "bookCall.err.phone": "올바른 10자리 미국 전화번호를 입력하세요.",
  "bookCall.err.email": "올바른 이메일 주소를 입력하거나 비워 두세요.",
  "bookCall.err.rateLimited":
    "이번 주에 이 네트워크에서 예약 요청이 너무 많습니다. 나중에 다시 시도하시거나 저희에게 전화해 주세요.",
  "bookCall.err.rateLimitedPhone":
    "이 전화번호는 이번 주 예약 한도에 도달했습니다. 다음 주에 다시 시도하시거나 저희에게 전화해 주세요.",
  "bookCall.err.submit": "통화를 예약하지 못했습니다. 정보를 확인한 후 다시 시도하세요.",
  "bookCall.entry.lead": "날짜와 시간을 직접 고르시겠어요?",
  "bookCall.entry.link": "통화 예약하기",
};

/**
 * Mandarin Chinese, Simplified (中文), a machine-assisted, best-effort translation.
 *
 * The legal and compliance-heavy strings (`enroll.consent`,
 * `enroll.consent.terms`, `enroll.consent.privacy`, `enroll.consent.pharmacy`,
 * `enroll.consent.broker`, `enroll.consent.brokerShare`, `enroll.consent.sms`,
 * `enroll.consent.sms.disclosure` and the `info.*` Medicare enrollment-window
 * block) require professional or legal review before production use.
 * Interpolation tokens (`{terms}`, `{privacy}`, `{email}`, `{org}`, `{member}`,
 * `{link}`, `{pharmacy}`, `{broker}`) and the literal STOP/HELP keywords stay
 * verbatim.
 */
const zh: Messages = {
  "disclaimer.banner":
    "本产品使用 Blue Button API，但未获得美国医疗保险和医疗补助服务中心 (CMS) 或美国卫生与公众服务部 (HHS) 的认可或认证。",
  "footer.terms": "条款与条件",

  "enroll.intro":
    "请简单介绍一下您自己和您的 Medicare 计划。我们的团队将在 1-2 个工作日内与您联系。标有 * 的字段为必填项。",
  "enroll.errorSummary": "提交前请修正下方高亮显示的字段。",
  "enroll.submitError": "抱歉，我们无法提交您的表单。请重试。",

  "enroll.success.title": "表单提交成功",
  "enroll.success.individual": "我们的团队将在 1-2 个工作日内与您联系。",
  "enroll.success.memberStatus": "您可以在{link}上查看您的注册状态。",
  "enroll.success.memberStatusLink": "会员页面",
  "enroll.success.partner": "此注册已通过 {org} 提交。",
  "enroll.success.staffPortal": "您是工作人员？返回您的门户",
  "enroll.success.self": "您通过 {org} 完成了注册。",
  "enroll.success.close": "完成",

  "section.patientInfo": "会员信息",
  "section.prescriptionRequest": "处方与请求",

  "section.personal": "个人信息",
  "section.permanent": "永久地址",
  "section.mailing": "邮寄地址",
  "section.contact": "联系信息",
  "section.medicare": "Medicare 信息",
  "section.pharmacy": "首选药房",
  "section.prompt": "是什么促使您提出请求？",

  "label.fullName": "法定全名",
  "label.firstName": "名字",
  "label.lastName": "姓氏",
  "label.suffix": "后缀（可选）",
  "label.pronouns": "首选代词（可选）",
  "label.dob": "出生日期",
  "label.gender": "性别",
  "label.street1": "街道地址",
  "label.street2": "公寓、套房、单元（可选）",
  "label.city": "城市",
  "label.state": "州",
  "label.zip": "邮政编码",
  "label.mailingDiffers": "我的邮寄地址与永久地址不同",
  "label.phone": "主要电话",
  "label.homePhone": "备用电话（可选）",
  "label.email": "电子邮件（可选）",
  "label.preferredLanguage": "后续通话时的首选语言",
  "label.partDPlan": "Part D 计划名称",
  "label.mbi": "MBI（Medicare 受益人标识符）（可选）",
  "label.planPhone": "Medicare 计划电话",
  "label.planFax": "Medicare 计划传真（可选）",
  "label.pharmacyName": "药房名称",
  "label.pharmacyStreet": "药房街道地址",

  "dob.subLabel": "与您 Medicare 卡上显示的一致。",
  "dob.month": "月",
  "dob.day": "日",
  "dob.year": "年",
  "dob.autoAdvance": "输入两位数字后会自动跳到下一个框。",

  "ph.fullName": "与您 Medicare 卡上显示的一致",
  "ph.suffix": "Jr, Sr., III",
  "ph.zip": "XXXXX",
  "ph.phone": "(555) 555-1234",
  "ph.planPhone": "(800) 555-1234",
  "ph.email": "you@example.com",
  "ph.partDPlan": "例如 AARP MedicareRx Preferred",
  "ph.mbi": "1EG4TE5MK73",
  "ph.pharmacyName": "例如 Walgreens #4521",
  "ph.prompt": "您的处方药或自付费用出现了什么问题？",

  "hint.permanentStreet": "不接受邮政信箱（PO Box）。Medicare 要求提供实际地址。",
  "hint.mbi": "请输入您 Medicare 卡上的 11 位代码（字母和数字）。",
  "mbi.subLabel": "现在提供可加快您的计划注册",
  "tooltip.preferredLanguage": "我们团队与您联系时将使用的语言。",

  "select.placeholder": "请选择…",
  "select.statePlaceholder": "—",
  "select.gender.male": "男",
  "select.gender.female": "女",
  "select.gender.non_binary": "非二元性别",
  "select.gender.prefer_not_to_say": "不愿透露",
  "select.language.english": "英语",
  "select.language.spanish": "西班牙语",
  "select.language.korean": "韩语",
  "select.language.mandarin": "中文（普通话）",
  "select.pronouns.placeholder": "选择…",
  "select.pronouns.she_her": "she/her",
  "select.pronouns.he_him": "he/him",
  "select.pronouns.they_them": "they/them",

  // Address autocomplete (Smarty) dropdown
  "addressAc.loading": "正在搜索地址…",
  "addressAc.noMatches": "没有匹配的地址",
  "addressAc.suggestions": "地址建议",

  // Plan section
  "section.plan": "Medicare 计划",
  "label.plan": "您的 Medicare 计划（可选）",
  "ph.plan": "按计划、保险公司或合同编号搜索",
  "plan.hint": "请先在上方填写您的州，然后选择您的计划。",
  "plan.loading": "正在加载计划…",
  "plan.noResults": "未找到您所在州的计划。",
  "plan.noMatches": "没有符合您搜索的计划。",
  "plan.refine": "继续输入以缩小列表范围。",

  "radio.yes": "是",
  "radio.no": "否",

  "btn.submit": "提交",
  "btn.submitting": "提交中…",

  "enroll.consent": "我同意{terms}和{privacy}。",
  "enroll.consent.terms": "条款与条件",
  "enroll.consent.privacy": "隐私政策",
  "enroll.consent.pharmacy": "我同意允许 {pharmacy} 与 DearBill 共享我的信息。",
  "enroll.consent.broker": "我同意指定 DearBill 为我的代表，并同意接受 DearBill 的联系。",
  "enroll.consent.brokerShare": "我同意与 {broker} 共享我的结果。",
  "enroll.broker.stateNotLicensed": "您的经纪人在该州没有执照，因此无法通过他们提交此表格。",
  "enroll.consent.sms": "我同意通过短信接收营销和状态更新信息。",
  "enroll.consent.sms.disclosure":
    "提供您的手机号码，即表示您同意接收来自 DearBill 的短信，包括双重身份验证（2FA）和安全代码、账户和注册通知、客户关怀和支持信息，以及营销和推广信息。信息频率因您的账户活动和偏好而异。可能会产生信息和数据费用。随时回复 STOP 即可退订并停止接收信息。回复 HELP 获取帮助，或通过 {email} 联系我们。有关我们如何处理您信息的详情，请参阅我们的{privacy}。",
  "enroll.consent.required": "请同意以继续。",

  "enroll.referral.banner": "经 {member} 转介，代表 {org} 进行注册。",
  "enroll.referral.expiresIn": "此转介链接将在以下时间后过期",
  "enroll.referral.expired": "您的转介链接已过期 — 请返回合作伙伴门户并开始新的注册。",
  "enroll.referral.invalid": "此转介链接无效 — 请返回合作伙伴门户并开始新的注册。",
  "enroll.referral.used.title": "此注册链接已被使用",
  "enroll.referral.used.body": "每个注册链接只能使用一次。请向转介您的机构索取新链接。",
  "enroll.referral.usedError": "此注册链接已被使用。请向转介您的机构索取新链接。",

  "enroll.self.banner": "欢迎！您正在通过 {org} 进行注册。",
  "enroll.self.invalid": "此注册代码无效 — 请重新扫描代码或联系向您提供该代码的机构。",
  "enroll.self.unavailable": "该机构目前不接受自助注册。请联系他们寻求帮助。",

  "info.trigger": "我什么时候可以注册？",
  "info.hide": "隐藏",
  "info.intro": "只有在规定的时间段内才允许注册 Part D 处方药计划：",
  "info.win.turn65.label": "当您首次年满 65 岁时",
  "info.win.turn65.body":
    "— 为期 7 个月的窗口期，涵盖您生日当月的前 3 个月、生日当月以及之后的 3 个月。",
  "info.win.fall.label": "秋季公开注册",
  "info.win.fall.body": "— 每年 10 月 15 日至 12 月 7 日。",
  "info.win.ma.label": "Medicare Advantage 转换期",
  "info.win.ma.body": "— 1 月 1 日至 3 月 31 日，但仅限您已参加 Medicare Advantage 计划的情况。",
  "info.beware.label": "请注意：",
  "info.beware.body": "住院并不会自动为您提供新的注册机会。",
  "info.snf":
    "如果您从专业护理机构、康复中心或长期护理机构出院，您有出院当月之后的 2 个月时间进行注册。",
  "info.hospital":
    "如果您在短期住院后从普通医院出院，即使您带着一份新的高价处方药回家，也无法在上述窗口期之外注册。等到下一个注册窗口期可能意味着您需要支付药物的全部费用以及永久性的延迟注册罚款。",
  "info.contact": "如果您有任何疑问，请直接联系 DearBill。",

  "a11y.required": "必填",

  "err.required": "必填",
  "err.invalidDate": "请输入一个过去的有效日期",
  "err.minAge": "会员必须年满 64 岁才能注册。",
  "err.selectOption": "请选择一个选项",
  "err.selectState": "请选择一个州",
  "err.zip": "邮政编码必须为 5 位数字（例如 90210 或 90210-1234）",
  "err.poBox": "永久地址不能是邮政信箱（PO Box）。您可以在下方的邮寄地址中使用邮政信箱",
  "err.streetIncomplete": "请输入完整的街道地址。",
  "err.zipMismatch": "此邮政编码与 {city}, {state} 不匹配。请检查。",
  "err.phone": "请输入 10 位数的美国电话号码",
  "err.email": "请输入有效的电子邮件",
  "err.dupPhone": "此电话号码已被另一个联系人使用",
  "err.dupEmail": "此电子邮件已被另一个联系人使用",
  "err.mbi": "请输入有效的 11 位 Medicare MBI",
  "err.promptMin": "请用一两句话告诉我们",

  "locale.aria": "语言",
  "locale.en": "English",
  "locale.es": "Español",
  "locale.ko": "한국어",
  "locale.zh": "中文",
  "locale.tooltip": "仅更改此页面的语言。",

  // “联系我们”弹窗。时间段的值按 canonical 值发送。
  // 机器辅助翻译，暂定稿，未经专业审校。
  "contact.trigger": "联系我们",
  "contact.title": "联系我们",
  "contact.tabs.call": "请求回电",
  "contact.tabs.message": "给我们留言",
  "contact.callUs": "更愿意打电话？",
  "contact.intro": "有疑问吗？您可以请求 DearBill 客服人员给您回电。填写此表格，我们会致电您。",
  "contact.when": "您希望我们何时给您打电话？",
  "contact.date": "日期",
  "contact.window": "时间",
  "contact.windowPlaceholder": "请选择时间",
  "contact.window.morning": "上午：美国东部时间上午 9 点至中午",
  "contact.window.afternoon": "下午：美国东部时间中午至下午 3 点",
  "contact.window.lateAfternoon": "傍晚：美国东部时间下午 3 点至 7 点",
  "contact.firstName": "名字",
  "contact.lastName": "姓氏",
  "contact.phone": "电话号码",
  "contact.zip": "邮政编码",
  "contact.consent": "勾选此框即表示您同意 DearBill 客服人员与您联系。",
  "contact.submit": "发送",
  "contact.submitting": "发送中…",
  "contact.cancel": "取消",
  "contact.success": "谢谢 — DearBill 客服人员将于 {date} 在您选择的时间段致电您。",
  "contact.done": "完成",
  "contact.err.date": "请选择明天起 30 天内的工作日。",
  "contact.err.dateWeekend": "我们在周一至周五致电 — 请选择工作日。",
  "contact.err.window": "请选择时间。",
  "contact.err.firstName": "请输入您的名字。",
  "contact.err.lastName": "请输入您的姓氏。",
  "contact.err.phone": "请输入您的电话号码。",
  "contact.err.zip": "请输入 5 位邮政编码。",
  "contact.err.consent": "请勾选此框，以便 DearBill 客服人员与您联系。",
  "contact.err.submit": "请检查您的信息后重试。",
  "contact.err.rateLimited": "您最近已请求过回电 — 请稍后再试。",
  // “给我们留言”标签页
  "contact.email": "电子邮件",
  "contact.topic": "主题",
  "contact.topicPlaceholder": "选择一个主题",
  "contact.message": "留言",
  "contact.success.message": "谢谢 — 我们会尽快与您联系。",
  "contact.err.contact": "请输入电子邮件或电话号码，以便我们回复您。",
  "contact.err.topic": "请选择一个主题。",
  "contact.err.message": "请输入留言。",
  "contact.topics.enrollment": "注册帮助",
  "contact.topics.eligibility": "资格问题",
  "contact.topics.technical": "技术问题",
  "contact.topics.other": "其他",
  // 预约通话页面。机器辅助翻译，暂定稿，未经专业审校。
  "bookCall.title": "预约通话",
  "bookCall.screening.title": "请求处方药费用帮助",
  "bookCall.backToEstimate": "返回费用估算",
  "bookCall.backToMedicines": "返回药物清单",
  "bookCall.screening.intro":
    "选择时间，了解处方药费用帮助。计算器中的答案不会随此请求发送。请求通话并不代表加入任何计划。",
  "bookCall.intro": "告诉我们何时致电，DearBill 专员会为您讲解可选方案。此次通话无需准备任何文件。",
  "bookCall.emailHint":
    "选填 — 如果与您 DearBill 记录中的电子邮件一致，我们会通过电子邮件发送确认。",
  "bookCall.date": "我们哪天给您打电话？",
  "bookCall.window": "一天中的什么时间？",
  "bookCall.window.morning": "上午",
  "bookCall.window.afternoon": "下午",
  "bookCall.window.lateAfternoon": "傍晚",
  "bookCall.hours.morning": "美国东部时间上午 9 点至中午",
  "bookCall.hours.afternoon": "美国东部时间中午至下午 3 点",
  "bookCall.hours.lateAfternoon": "美国东部时间下午 3 点至 7 点",
  "bookCall.submit": "预约我的通话",
  "bookCall.submitting": "预约中…",
  "bookCall.success.title": "预约成功！",
  "bookCall.success.body":
    "您已预约在 {date}{window}通话。如果您同意接收我们的短信，我们会通过短信确认。",
  "bookCall.err.window": "请选择一个时间段。",
  "bookCall.err.firstName": "请输入您的名字，最多 100 个字符。",
  "bookCall.err.lastName": "请输入您的姓氏，最多 100 个字符。",
  "bookCall.err.phone": "请输入有效的 10 位美国电话号码。",
  "bookCall.err.email": "请输入有效的电子邮件地址，或留空。",
  "bookCall.err.rateLimited": "本周来自此网络的预约请求过多。请稍后再试，或致电我们。",
  "bookCall.err.rateLimitedPhone": "此电话号码已达到每周预约上限。请下周再试，或致电我们。",
  "bookCall.err.submit": "我们无法预约您的通话。请检查您的信息后重试。",
  "bookCall.entry.lead": "想自己选择日期和时间？",
  "bookCall.entry.link": "预约通话",
};

export const MESSAGES: Record<Locale, Messages> = { en, es, ko, zh };
